import { useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { Instagram, Facebook, Dribbble, ArrowRight, Mail, Phone, MapPin, Clock } from 'lucide-react'
import Breadcrumb from '../components/Breadcrumb'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Link } from 'react-router'

gsap.registerPlugin(ScrollTrigger)

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', company: '', website: '', phone: '', email: '', service: '', otherService: '', budget: '', timeline: '', message: '' })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sectionRef = useRef<HTMLDivElement>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const finalService = formData.service === 'other' ? formData.otherService : formData.service

    try {
      const payload = {
        to: "shazi.ssuet@gmail.com",
        subject: `Technifire - New Contact from ${formData.name}`,
        template: "simple template",
        variables: {
          content: `<h2>New Contact Form Submission</h2>
<p><strong>Name:</strong> ${formData.name}</p>
<p><strong>Company:</strong> ${formData.company}</p>
<p><strong>Website:</strong> ${formData.website}</p>
<p><strong>Email:</strong> ${formData.email}</p>
<p><strong>Phone:</strong> ${formData.phone}</p>
<p><strong>Service Needed:</strong> ${finalService}</p>
<p><strong>Budget:</strong> ${formData.budget}</p>
<p><strong>Timeline:</strong> ${formData.timeline}</p>
<p><strong>Message:</strong><br/>${formData.message}</p>`,
          primaryColor: "#B7000E",
          projectName: "Technifire",
          year: new Date().getFullYear().toString()
        },
        from_name: "Technifire",
        from_email: "info@technifire.io"
      };

      const response = await fetch('https://api.agentifire.io/api/public-email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to send message');

      setSubmitted(true)
      setFormData({ name: '', company: '', website: '', phone: '', email: '', service: '', otherService: '', budget: '', timeline: '', message: '' })
    } catch (err: any) {
      setError(err.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => setSubmitted(false), 5000)
      return () => clearTimeout(timer)
    }
  }, [submitted])

  useEffect(() => {
    const ctx = gsap.context(() => {
      const elements = sectionRef.current?.querySelectorAll('.contact-animate')
      if (elements && elements.length > 0) {
        gsap.fromTo(elements, { y: 40, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top bottom', toggleActions: 'play none none none', invalidateOnRefresh: true }
        })
      }
    }, sectionRef)
    return () => ctx.revert()
  }, [])

  return (
    <div className="min-h-screen bg-[#050505] pb-20" ref={sectionRef}>
      <Helmet>
        <title>Contact Us — Technifire Software</title>
        <meta name="description" content="Get in touch with Technifire Software. Start your next web development, SaaS, AI, or digital project. Based in Kansas City, MO — serving clients worldwide." />
        <link rel="canonical" href="https://technifire.io/contacts" />
        <meta property="og:title" content="Contact Us — Technifire Software" />
        <meta property="og:description" content="Ready to build something great? Contact Technifire Software for web development, SaaS, AI, and digital growth solutions." />
        <meta property="og:url" content="https://technifire.io/contacts" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Breadcrumb current="CONTACT US" />

      {/* Hero */}
      <div className="px-6 lg:px-10 pt-8 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div className="contact-animate">
            <span className="text-mono text-[#9A9A9A] mb-3 block">CONTACT US</span>
            <h1 className="text-h2 uppercase text-white mb-4">
              WANT A PROJECT<br />THAT WORKS?
            </h1>
            <p className="text-lg text-[#9A9A9A] mb-8">
              Tell us about your project. We will respond within 24 hours with a focused strategy call and a practical recommended next step.
            </p>

            {/* Contact Info Cards */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#9A9A9A]" />
                </div>
                <div>
                  <p className="text-mono text-[#9A9A9A] text-[10px]">EMAIL</p>
                  <a href="mailto:info@technifire.io" className="text-white hover:text-[#9A9A9A] transition-colors">info@technifire.io</a>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-[#9A9A9A]" />
                </div>
                <div>
                  <p className="text-mono text-[#9A9A9A] text-[10px]">PHONE</p>
                  <a href="tel:+14255414001" className="text-white hover:text-[#9A9A9A] transition-colors">+1 (425) 541-4001</a>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#9A9A9A]" />
                </div>
                <div>
                  <p className="text-mono text-[#9A9A9A] text-[10px]">LOCATION</p>
                  <p className="text-white">8301 State Line Rd Ste 220 # 3747, Kansas City, MO 64114-2025</p>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-[#9A9A9A]" />
                </div>
                <div>
                  <p className="text-mono text-[#9A9A9A] text-[10px]">RESPONSE TIME</p>
                  <p className="text-white">Within 24 hours</p>
                </div>
              </div>
            </div>

            {/* Social icons */}
            <div className="flex items-center gap-3 mt-6">
              <a href="https://instagram.com/technifire.io" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <Link to="https://www.facebook.com/technifire.io" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-colors">
                <Facebook className="w-4 h-4" />
              </Link>
              <a href="https://behance.net/technifire" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-colors">
                <span className="text-xs font-bold">Be</span>
              </a>
              <a href="https://dribbble.com/technifire" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-colors">
                <Dribbble className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Form */}
          <div className="contact-animate">
            {submitted ? (
              <div className="bg-[#0a0a0a] rounded-2xl p-10 border border-[#4ADE80]/20 text-center">
                <div className="w-16 h-16 rounded-full bg-[#4ADE80]/10 flex items-center justify-center mx-auto mb-4">
                  <ArrowRight className="w-8 h-8 text-[#4ADE80]" />
                </div>
                <h3 className="text-2xl text-white mb-2">Message Sent!</h3>
                <p className="text-[#9A9A9A]">Thank you for reaching out. We will get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-[#0a0a0a] rounded-2xl p-6 lg:p-8 border border-white/5">
                <h3 className="text-xl text-white mb-6">Project Inquiry</h3>

                <div className="space-y-5">
                  <div>
                    <label className="text-mono text-[#9A9A9A] text-[10px] block mb-2">YOUR NAME</label>
                    <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Smith"
                      className="w-full bg-transparent border-b border-white/20 text-white text-lg py-2 focus:border-white/50 focus:outline-none transition-colors placeholder:text-white/15" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-mono text-[#9A9A9A] text-[10px] block mb-2">COMPANY *</label>
                      <input type="text" required value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} placeholder="Company name" className="w-full bg-transparent border-b border-white/20 text-white text-lg py-2 focus:border-white/50 focus:outline-none placeholder:text-white/15" />
                    </div>
                    <div>
                      <label className="text-mono text-[#9A9A9A] text-[10px] block mb-2">WEBSITE</label>
                      <input type="url" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://" className="w-full bg-transparent border-b border-white/20 text-white text-lg py-2 focus:border-white/50 focus:outline-none placeholder:text-white/15" />
                    </div>
                  </div>

                  <div>
                    <label className="text-mono text-[#9A9A9A] text-[10px] block mb-2">PHONE *</label>
                    <input type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1 (555) 000-0000"
                      className="w-full bg-transparent border-b border-white/20 text-white text-lg py-2 focus:border-white/50 focus:outline-none transition-colors placeholder:text-white/15" />
                  </div>

                  <div>
                    <label className="text-mono text-[#9A9A9A] text-[10px] block mb-2">EMAIL</label>
                    <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@company.com"
                      className="w-full bg-transparent border-b border-white/20 text-white text-lg py-2 focus:border-white/50 focus:outline-none transition-colors placeholder:text-white/15" />
                  </div>

                  <div>
                    <label className="text-mono text-[#9A9A9A] text-[10px] block mb-2">SERVICE NEEDED</label>
                    <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })} required>
                      <SelectTrigger className="w-full bg-transparent border border-white/20 text-white text-lg py-3 px-4 transition-colors rounded-xl shadow-none h-auto [&_svg]:text-white/50">
                        <SelectValue placeholder="Select a service..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111] border border-white/10 text-white rounded-xl mt-10">
                        <SelectItem value="website" className="text-white focus:bg-[#B7000E] focus:text-white hover:bg-[#B7000E] hover:text-white data-[highlighted]:bg-[#B7000E] data-[highlighted]:text-white cursor-pointer">Website Development</SelectItem>
                        <SelectItem value="branding" className="text-white focus:bg-[#B7000E] focus:text-white hover:bg-[#B7000E] hover:text-white data-[highlighted]:bg-[#B7000E] data-[highlighted]:text-white cursor-pointer">Branding & Design</SelectItem>
                        <SelectItem value="crm" className="text-white focus:bg-[#B7000E] focus:text-white hover:bg-[#B7000E] hover:text-white data-[highlighted]:bg-[#B7000E] data-[highlighted]:text-white cursor-pointer">CRM Systems</SelectItem>
                        <SelectItem value="ecommerce" className="text-white focus:bg-[#B7000E] focus:text-white hover:bg-[#B7000E] hover:text-white data-[highlighted]:bg-[#B7000E] data-[highlighted]:text-white cursor-pointer">E-Commerce</SelectItem>
                        <SelectItem value="landing" className="text-white focus:bg-[#B7000E] focus:text-white hover:bg-[#B7000E] hover:text-white data-[highlighted]:bg-[#B7000E] data-[highlighted]:text-white cursor-pointer">Landing Page</SelectItem>
                        <SelectItem value="saas" className="text-white focus:bg-[#B7000E] focus:text-white hover:bg-[#B7000E] hover:text-white data-[highlighted]:bg-[#B7000E] data-[highlighted]:text-white cursor-pointer">SaaS Platform</SelectItem>
                        <SelectItem value="ai" className="text-white focus:bg-[#B7000E] focus:text-white hover:bg-[#B7000E] hover:text-white data-[highlighted]:bg-[#B7000E] data-[highlighted]:text-white cursor-pointer">AI Automation & Chatbots</SelectItem>
                        <SelectItem value="other" className="text-white focus:bg-[#B7000E] focus:text-white hover:bg-[#B7000E] hover:text-white data-[highlighted]:bg-[#B7000E] data-[highlighted]:text-white cursor-pointer">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.service === 'other' && (
                    <div className="fade-in-up">
                      <label className="text-mono text-[#9A9A9A] text-[10px] block mb-2">SPECIFY OTHER SERVICE *</label>
                      <input type="text" required value={formData.otherService} onChange={(e) => setFormData({ ...formData, otherService: e.target.value })} placeholder="What service are you looking for?"
                        className="w-full bg-transparent border-b border-white/20 text-white text-lg py-2 focus:border-white/50 focus:outline-none transition-colors placeholder:text-white/15" />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-mono text-[#9A9A9A] text-[10px] block mb-2">ESTIMATED BUDGET *</label>
                      <select required value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} className="w-full bg-[#0a0a0a] border-b border-white/20 text-white py-3 focus:outline-none">
                        <option value="">Select range</option><option>$1,000–$2,500</option><option>$2,500–$5,000</option><option>$5,000–$10,000</option><option>$10,000–$25,000</option><option>$25,000+</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-mono text-[#9A9A9A] text-[10px] block mb-2">TARGET TIMELINE *</label>
                      <select required value={formData.timeline} onChange={(e) => setFormData({ ...formData, timeline: e.target.value })} className="w-full bg-[#0a0a0a] border-b border-white/20 text-white py-3 focus:outline-none">
                        <option value="">Select timeline</option><option>Immediately</option><option>Within 30 days</option><option>1–3 months</option><option>3–6 months</option><option>Researching options</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-mono text-[#9A9A9A] text-[10px] block mb-2">PROJECT DETAILS</label>
                    <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={3} placeholder="Tell us about your project, timeline, and goals..." required
                      className="w-full bg-transparent border-b border-white/20 text-white text-base py-2 focus:border-white/50 focus:outline-none transition-colors resize-none placeholder:text-white/15" />
                  </div>

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm p-4 rounded-xl">
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-4 disabled:opacity-50 disabled:cursor-not-allowed">
                    {loading ? 'SENDING...' : 'SEND MESSAGE'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
